/**
 * TencentCosSection — org-wide Tencent Cloud Object Storage credentials
 * (extracted from the old god component's `renderTencent`).
 *
 * SecretId/SecretKey are write-only Vault fields; bucket/region/folder
 * values round-trip. The bucket picker prefers a live `cos:ListBuckets`
 * dropdown and falls back to free-text with inline regex validation when
 * the scope is unavailable or there are no credentials yet. The "locations
 * routed here without credentials" alert — previously a page-level sticky
 * banner above the whole Advanced tab — now renders at the top of this
 * section's own content, since it only concerns this section.
 */
import { useEffect, useState } from 'react';
import { Cloud, Loader2, Send, ShieldCheck, ShieldAlert, Info, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SecretInput } from '@/components/ui/secret-input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { trpc } from '@/lib/trpc';
import { SettingsSection } from '../components/console/SettingsSection';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';

type TencentCosRegion = 'ap-guangzhou' | 'ap-shanghai' | 'ap-shenzhen-fsi' | 'ap-beijing' | 'ap-singapore';
const TENCENT_REGIONS: ReadonlyArray<TencentCosRegion> = [
  'ap-guangzhou',
  'ap-shanghai',
  'ap-shenzhen-fsi',
  'ap-beijing',
  'ap-singapore',
];
const TENCENT_BUCKET_RE = /^[a-z0-9-]+-\d{10,}$/;

export function TencentCosSection() {
  const settingsQuery = trpc.productionSettings.getTencentCosSettings.useQuery();
  const updateMutation = trpc.productionSettings.updateTencentCosSettings.useMutation();
  const testMutation = trpc.productionSettings.testTencentCos.useMutation();
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();
  const bucketsListQuery = trpc.printAgent.listTencentBuckets.useQuery(undefined, {
    enabled: settingsQuery.data?.hasCredentials === true,
    staleTime: 60_000,
  });

  const [secretId, setSecretId] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [bucket, setBucket] = useState('');
  const [region, setRegion] = useState<TencentCosRegion | ''>('');
  const [customsFolder, setCustomsFolder] = useState('');
  const [printsFolder, setPrintsFolder] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (settingsQuery.data && !loaded) {
      const loadedRegion = settingsQuery.data.region;
      if (loadedRegion && (TENCENT_REGIONS as readonly string[]).includes(loadedRegion)) {
        setRegion(loadedRegion as TencentCosRegion);
      }
      setBucket(settingsQuery.data.bucket ?? '');
      setCustomsFolder(settingsQuery.data.customsFolder ?? '');
      setPrintsFolder(settingsQuery.data.printsFolder ?? '');
      setLoaded(true);
    }
  }, [settingsQuery.data, loaded]);

  const bucketError =
    bucket && !TENCENT_BUCKET_RE.test(bucket) ? 'Format: <name>-<appid> (e.g. mz-prints-1300000000)' : null;
  const bucketValid = bucketError === null;

  const hasCredentials = settingsQuery.data?.hasCredentials === true;

  const isDirty =
    loaded &&
    (secretId !== '' ||
      secretKey !== '' ||
      bucket !== (settingsQuery.data?.bucket ?? '') ||
      region !== (settingsQuery.data?.region ?? '') ||
      customsFolder !== (settingsQuery.data?.customsFolder ?? '') ||
      printsFolder !== (settingsQuery.data?.printsFolder ?? ''));

  useDirtyRegistration(
    'tencent-cos',
    isDirty
      ? {
          label: 'Tencent Cloud Storage',
          save: async () => {
            if (!bucketValid) {
              throw new Error('Fix the bucket format first');
            }
            const payload: {
              secretId?: string;
              secretKey?: string;
              bucket?: string;
              region?: TencentCosRegion;
              customsFolder?: string | null;
              printsFolder?: string | null;
            } = {};
            if (secretId) payload.secretId = secretId;
            if (secretKey) payload.secretKey = secretKey;
            if (bucket && bucket !== (settingsQuery.data?.bucket ?? '')) {
              payload.bucket = bucket;
            }
            if (region && region !== (settingsQuery.data?.region ?? '')) {
              payload.region = region;
            }
            if (customsFolder !== (settingsQuery.data?.customsFolder ?? '')) {
              payload.customsFolder = customsFolder === '' ? null : customsFolder;
            }
            if (printsFolder !== (settingsQuery.data?.printsFolder ?? '')) {
              payload.printsFolder = printsFolder === '' ? null : printsFolder;
            }
            await updateMutation.mutateAsync(payload);
            setSecretId('');
            setSecretKey('');
            await settingsQuery.refetch();
          },
          discard: () => {
            setSecretId('');
            setSecretKey('');
            const serverRegion = settingsQuery.data?.region;
            setRegion(
              serverRegion && (TENCENT_REGIONS as readonly string[]).includes(serverRegion)
                ? (serverRegion as TencentCosRegion)
                : '',
            );
            setBucket(settingsQuery.data?.bucket ?? '');
            setCustomsFolder(settingsQuery.data?.customsFolder ?? '');
            setPrintsFolder(settingsQuery.data?.printsFolder ?? '');
          },
        }
      : null,
  );

  const handleTest = () => {
    testMutation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.success) {
          toast.success('Connection successful', { description: 'Tencent COS credentials are valid.' });
        } else {
          const errorResult = result as { success: false; error: string };
          toast.error('Connection failed', { description: errorResult.error });
        }
      },
      onError: (error) => toast.error('Test failed', { description: error.message }),
    });
  };

  const tencentLocationsCount = (locationsQuery.data ?? []).filter(
    (l) => l.printFileSource === 'tencent_cos',
  ).length;
  const showMissingAlert = tencentLocationsCount > 0 && !hasCredentials;

  return (
    <SettingsSection
      id="tencent-cos"
      title="Tencent Cloud Storage"
      description="Mainland Tencent COS credentials for sub-5s print fetches in China."
      icon={Cloud}
      scope="org"
    >
      {settingsQuery.isPending ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          {showMissingAlert ? (
            <Alert
              variant="default"
              className="border-amber-300 bg-amber-50 text-amber-900 [&>svg]:text-amber-700"
            >
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Tencent Cloud Storage credentials missing</AlertTitle>
              <AlertDescription>
                {tencentLocationsCount === 1
                  ? '1 location is set to use Tencent Cloud Storage but no credentials are configured. Print jobs will fail until you save credentials below.'
                  : `${tencentLocationsCount} locations are set to use Tencent Cloud Storage but no credentials are configured. Print jobs will fail until you save credentials below.`}
              </AlertDescription>
            </Alert>
          ) : null}

          {hasCredentials ? (
            <div role="status" className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-emerald-800">Credentials are securely stored.</p>
                <p className="text-xs text-emerald-700/80">To update, enter new values below.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-800">No credentials configured.</p>
                <p className="text-xs text-amber-700/80">Enter SecretId, SecretKey, bucket, and region below.</p>
              </div>
            </div>
          )}

          <p className="text-sm text-muted-foreground/90 leading-relaxed">
            Mainland Tencent Cloud account. Hosts files inside the Great Firewall for sub-5 second print
            fetches in China.
          </p>

          {hasCredentials && tencentLocationsCount === 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                No locations are using Tencent COS yet. Set &lsquo;Print file source&rsquo; on a location to
                route prints here.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="tencent-secret-id" className="text-sm font-medium">
              SecretId
            </Label>
            <SecretInput
              id="tencent-secret-id"
              placeholder={hasCredentials ? '••••••••' : 'Enter Tencent SecretId'}
              value={secretId}
              onChange={(e) => setSecretId(e.target.value)}
              className="h-10 font-mono text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tencent-secret-key" className="text-sm font-medium">
              SecretKey
            </Label>
            <SecretInput
              id="tencent-secret-key"
              placeholder={hasCredentials ? '••••••••' : 'Enter Tencent SecretKey'}
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              className="h-10 font-mono text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tencent-bucket" className="text-sm font-medium">
              Default bucket
            </Label>
            {hasCredentials && bucketsListQuery.data?.supported === true && bucketsListQuery.data.buckets.length > 0 ? (
              <Select value={bucket} onValueChange={(v) => setBucket(v)}>
                <SelectTrigger id="tencent-bucket" className="h-10 font-mono text-sm">
                  <SelectValue placeholder="Select a bucket" />
                </SelectTrigger>
                <SelectContent>
                  {bucketsListQuery.data.buckets.map((b) => (
                    <SelectItem key={b.name} value={b.name}>
                      <span className="font-mono">{b.name}</span>
                      {b.region && <span className="text-xs text-muted-foreground"> · {b.region}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="tencent-bucket"
                type="text"
                placeholder="mz-prints-1300000000"
                value={bucket}
                onChange={(e) => setBucket(e.target.value)}
                className={`h-10 font-mono text-sm ${bucketError ? 'border-destructive' : ''}`}
                aria-invalid={bucketError !== null}
                aria-describedby="tencent-bucket-helper"
                autoComplete="off"
              />
            )}
            <p
              id="tencent-bucket-helper"
              className={`text-sm ${bucketError ? 'text-destructive' : 'text-muted-foreground/90'}`}
            >
              {bucketError ??
                (hasCredentials && bucketsListQuery.data?.supported === true
                  ? 'Owner-direct default. Per-client buckets are set on the connection rows under each location.'
                  : 'Format: <name>-<appid> (e.g. mz-prints-1300000000)')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tencent-region" className="text-sm font-medium">
              Region
            </Label>
            <Select value={region} onValueChange={(v) => setRegion(v as TencentCosRegion)}>
              <SelectTrigger id="tencent-region" className="h-10 text-sm">
                <SelectValue placeholder="Select region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ap-guangzhou">ap-guangzhou (Guangzhou)</SelectItem>
                <SelectItem value="ap-shanghai">ap-shanghai (Shanghai)</SelectItem>
                <SelectItem value="ap-shenzhen-fsi">ap-shenzhen-fsi (Shenzhen Finance)</SelectItem>
                <SelectItem value="ap-beijing">ap-beijing (Beijing)</SelectItem>
                <SelectItem value="ap-singapore">ap-singapore (Singapore, intl)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground/90">
              Pick the region that matches your COS account. Singapore is the supported intl region; the
              others are mainland China.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tencent-customs-folder" className="text-sm font-medium">
              Customs folder
            </Label>
            <Input
              id="tencent-customs-folder"
              type="text"
              placeholder="custom"
              value={customsFolder}
              onChange={(e) => setCustomsFolder(e.target.value)}
              className="h-10 font-mono text-sm transition-colors"
              autoComplete="off"
              aria-describedby="tencent-customs-folder-helper"
            />
            <p id="tencent-customs-folder-helper" className="text-sm text-muted-foreground/90">
              Where CUST-* files are stored. Default <code>custom</code>. Nested paths allowed (e.g.{' '}
              <code>designs/customs</code>).
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tencent-prints-folder" className="text-sm font-medium">
              Prints folder
            </Label>
            <Input
              id="tencent-prints-folder"
              type="text"
              placeholder="(empty, bucket root)"
              value={printsFolder}
              onChange={(e) => setPrintsFolder(e.target.value)}
              className="h-10 font-mono text-sm transition-colors"
              autoComplete="off"
              aria-describedby="tencent-prints-folder-helper"
            />
            <p id="tencent-prints-folder-helper" className="text-sm text-muted-foreground/90">
              Where standard product files are stored. Tencent default is bucket root.
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={!hasCredentials || testMutation.isPending}
              className="h-10 min-w-[120px] gap-2"
            >
              {testMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Test Connection
            </Button>
          </div>
        </div>
      )}
    </SettingsSection>
  );
}
